import React, { useState, useEffect } from 'react';
import c from 'classnames';
import styles from './UploadForm.css';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { uploadFiles } from '../../apis/dumpert';
import { Loading } from '../loading';
import { ClickElement, InputField } from '../form';
import { SlideIn, SlideInHeader } from '../slidein';
import { TipForm } from '../tipform';
import {
  selectUploadFormActive,
  changeUploadFormAction,
} from '../../ducks';
import { UPLOADFORM } from '../../lib/labels';
import * as ReactGA from 'react-ga';

const uploadGAEvent = data => {
  ReactGA.event({
    category: 'Other',
    action: 'Upload',
    label: JSON.stringify([...data.map(file => file.name)]),
  });
};

export const UploadForm = () => {
  const uploadDataInital = { 
    name: '', 
    email: '', 
    title: '',
    description: '', 
    accept: false,
    files: [], 
  };

  const uploadFormActive = useSelector(selectUploadFormActive);
  const [dragOver, setDragOver] = useState(false);
  const [uploadData, setUploadData] = useState(uploadDataInital);
  const [submitState, setSubmitState] = useState(false);
  const [error, setError] = useState(undefined);
  const [result, setResult] = useState(undefined); 
  const dispatch = useDispatch();

  const submit = e => {
    e.preventDefault();
    const formData = new FormData(e.target);
    formData.delete('files');
    
    if (uploadData.files) {
      const files = uploadData.files;
      for(let i = 0; i < files.length; i++) {
        formData.append(`file${i + 1}`, files[i].file);
      }
    }

    setError(undefined);
    setSubmitState('uploading');

    const errorMessages = {
      0: 'Er gaat iets mis... probeer het nog eens',
      400: `Er gaat iets mis... 
      Probeer je de juiste bestanden te uploaden? 
      Wij accepteren alleen plaatjes & video's.`,
      413: 'Er gaat iets mis... je bestand is te groot',
    };

    return uploadFiles(formData)
      .then(response => {
        switch (response.status) {
        case 400:
          setError({ message: errorMessages[400] }); 
          break;
        case 413:
          setError({ message: errorMessages[413] }); 
          break;           
        default: {
          response.json()
            .then(() => setResult(true))
            .catch(() => {
              setError({ message: errorMessages[0] }); 
            });
        } break;
        }
      })
      .catch(() => {
        setError({ message: errorMessages[0] }); 
      })
      .finally(() => {
        uploadGAEvent(uploadData.files);
        setSubmitState('done');
      });
    
  };

  const filesHandleChange = uploadFiles => {
    if (uploadFiles) {
      const fileArray = [...uploadData.files, ...uploadFiles.map(file => {
        return ({
          name: file.name,
          src: file.type.includes('video') ? 'video' : URL.createObjectURL(file),
          file,
        });
      }),
      ];

      const filterArray = array => array.reduce((acc, current) => {
        if (!acc.some(item => item.name === current.name)) {
          acc.push(current);
        }
        return acc;
      }, []);

      setUploadData({
        ...uploadData,
        files: [...filterArray(fileArray)],
      });
    }
  };

  const imageDelete = deleteIndex => {
    setUploadData({ 
      ...uploadData, 
      files: [...uploadData.files.filter((a, index) => index !== deleteIndex)],
    });
  };

  useEffect(() => {
    if (result && submitState === 'done') { 
      setUploadData(uploadDataInital);
    }
  }, [result, submitState]);

  return (
    <>
      <SlideIn isActive={uploadFormActive} changeActiveState={changeUploadFormAction}>
        <div className={styles.uploadform}>
          <div className={c(styles.uploadform_container, styles.submitted)}>
            <SlideInHeader
              titleText={UPLOADFORM.TITLE.FORM}
              clickHandler={() => dispatch(changeUploadFormAction(!uploadFormActive))}
            /> 
            {submitState === 'uploading' && (
              <div className={styles.uploading}>
                <Loading
                  whatIsLoading={`item${uploadData.files.length > 1 ? 's' : ''} uploaden`}
                  showSuffix={false}
                />
              </div>
            )}
            {result && !error && (              
              <div className={c(styles.notification, styles.success)}>
                <div>Je upload is succesvol verstuurd.</div>
                {submitState === 'done' && (
                  <div className={styles.close} onClick={() => {
                    setResult(undefined);
                    setSubmitState(false);
                  }}>
                    <FontAwesomeIcon icon={['fas', 'times']} size="lg" />
                  </div>
                )}
              </div>
            )}
            {error && (
              <div className={c(styles.notification, styles.error)}>
                <div>{error.message}</div>
                <div className={styles.close} onClick={() => {
                  setError(undefined);
                }} >
                  <FontAwesomeIcon icon={['fas', 'times']} size="lg" />
                </div>
              </div>
            )}
            <form id={'uploadform'} onSubmit={submit} encType="multipart/form-data" className={c(
              { [styles.hideform]: submitState === 'uploading'},
            )}>
              <div className={styles.fields}>
                <input type="hidden" name="nr_of_files" value={uploadData.files.length} />
                <InputField
                  name="user_name"
                  type="text"
                  placeholder={UPLOADFORM.PLACEHOLDER.NAME}
                  autoComplete="username"
                  value={uploadData.name}
                  onChange={e => setUploadData({ ...uploadData, name: e.target.value })}
                  required
                />
                <InputField
                  name="user_email"
                  type="email"
                  placeholder={UPLOADFORM.PLACEHOLDER.EMAIL}
                  autoComplete="email"
                  value={uploadData.email}
                  onChange={e => setUploadData({ ...uploadData, email: e.target.value })}
                  required
                />
                <InputField
                  name="title"
                  type="text"
                  placeholder={UPLOADFORM.PLACEHOLDER.TITLE}
                  autoComplete="off"
                  value={uploadData.title}
                  onChange={e => setUploadData({ ...uploadData, title: e.target.value })}
                  required
                />
                <textarea
                  name="uldescription"
                  className={'textarea'}
                  rows={'5'}
                  placeholder={UPLOADFORM.PLACEHOLDER.DESCRIPTION}
                  autoComplete="off"
                  maxLength="150"
                  value={uploadData.description}
                  onChange={e => setUploadData({ ...uploadData, description: e.target.value })}
                /> 
                <div className={styles['desc-length']}>{uploadData.description.length}{UPLOADFORM.NUM_OF_CHARS}</div>
                <div className={styles['files-uploader']}>
                  <div className={c(styles['file-drop-area'], { [styles.dragover]: dragOver })}>
                    <input 
                      name="files"
                      type="file"
                      placeholder='files'
                      autoComplete="off"
                      multiple
                      onDragOver={e => {
                        setDragOver(true);
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={() => setDragOver(false)}
                      onChange={e => {
                        const uploadFiles = Array.from(e.target.files);
                        filesHandleChange(uploadFiles);
                      }} 
                      className={c({ 'dragover': dragOver })}
                      required
                    /> 
                    <div className={styles['helper-text']}>{UPLOADFORM.UPLOAD_AREA}</div>           
                  </div>
                  {uploadData.files.map((item, index) => (
                    <div className={styles['files-list']} key={item.name}>
                      <div>
                        <div>
                          {item.src === 'video' 
                            ? <FontAwesomeIcon icon={['fas', 'film']} size="2x" />
                            : <img src={item.src} alt={item.name} />
                          }
                        </div>
                        <div>
                          {item.name}
                        </div>
                        <div className={styles['delete-file']} onClick={() => 
                          imageDelete(index)}>
                          <FontAwesomeIcon icon="times" size="2x" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className={styles.accept}>
                  <label>
                    <input 
                      type="checkbox" 
                      name="accept_conditions" 
                      value="1" 
                      required 
                      checked={uploadData.accept} 
                      onChange={e => setUploadData({ ...uploadData, accept: e.target.checked })} 
                    />
                    <input type="hidden" name="must_accept_conditions" value="1" />
                    <span>{UPLOADFORM.CONDITIONS.ACCEPT}&nbsp;</span>
                    <a href="https://assets.dumpert.nl/algemene-gebruiksvoorwaarden-dtmedia.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer">
                      {UPLOADFORM.CONDITIONS.LINK}
                    </a>
                  </label>
                  <ClickElement
                    ElementType="button"
                    type="submit"
                    text={UPLOADFORM.CONDITIONS.BUTTON}
                    primary
                  />
                </div>
              </div>
            </form>
            <TipForm />
          </div>
        </div>
      </SlideIn>
    </>
  );
};
